import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import MenuManagement from './MenuManagement';
import Settings from './Settings';
import SubMenuManagement from './SubMenuManagement';
import Analytics from './components/Analytics';
import KnowledgeBaseManager from './components/KnowledgeBaseManager';
import ImageManagement from './components/ImageManagement';
import WidgetSettings from './components/WidgetSettings';
import WebsiteScraping from './components/WebsiteScraping';
import VisitorsManagement from './VisitorsManagement';
import LiveChat from './LiveChat';
import AcronymsManagement from './AcronymsManagement';
import { liveChatAPI } from './api';

function Dashboard({ user, onLogout }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [dashboardLogo, setDashboardLogo] = useState(null);
    const [liveChatUnread, setLiveChatUnread] = useState(0);

    useEffect(() => {
        fetch('/api/settings/logos')
            .then(res => res.json())
            .then(data => {
                if (data.dashboard_logo) {
                    setDashboardLogo(data.dashboard_logo);
                }
            })
            .catch(err => console.error('Error fetching logo:', err));
    }, []);

    useEffect(() => {
        let cancelled = false;
        const tick = async () => {
            try {
                const res = await liveChatAPI.stats();
                const data = res.data?.data;
                if (!cancelled && data) {
                    setLiveChatUnread((data.waiting_count || 0) + (data.unread_count || 0));
                }
            } catch (e) { /* noop */ }
        };
        tick();
        const t = setInterval(tick, 30000);
        return () => { cancelled = true; clearInterval(t); };
    }, [location.pathname]);

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        onLogout();
        navigate('/');
    };

    const menuItems = [
        { path: '/', icon: '📊', label: 'Dashboard' },
        { path: '/analytics', icon: '📈', label: 'Analytics' },
        { path: '/live-chat', icon: '💬', label: 'Live Chat', badge: liveChatUnread },
        { path: '/menus', icon: '📋', label: 'Menus' },
        { path: '/sub-menus', icon: '📑', label: 'Sub Menus' },
        { path: '/images', icon: '🖼️', label: 'Images' },
        { path: '/knowledge-base', icon: '🤖', label: 'AI Knowledge Base' },
        { path: '/acronyms', icon: '🔤', label: 'Acronyms' },
        { path: '/website-scraping', icon: '🌐', label: 'Website Scraping' },
        { path: '/widget-settings', icon: '🎨', label: 'Widget Settings' },
        { path: '/settings', icon: '⚙️', label: 'Settings' },
        { path: '/visitors', icon: '👥', label: 'Visitors' },
    ];

    return (
        <div style={{ display: 'flex', minHeight: '100vh', background: '#f8f9fa' }}>
            {/* Solid Dark Sidebar */}
            <aside style={{
                width: '280px',
                background: 'linear-gradient(180deg, #1a1a1a 0%, #0f0f0f 100%)',
                padding: '24px 16px',
                position: 'sticky',
                top: 0,
                height: '100vh',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '4px 0 24px rgba(0, 0, 0, 0.3)'
            }}>
                {/* Logo Section */}
                <div style={{ 
                    marginBottom: '32px', 
                    textAlign: 'center', 
                    paddingBottom: '20px', 
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)' 
                }}>
                    {dashboardLogo ? (
                        <img 
                            src={dashboardLogo} 
                            alt="Logo" 
                            style={{ 
                                maxHeight: '60px', 
                                maxWidth: '100%', 
                                filter: 'brightness(0) invert(1)' 
                            }} 
                        />
                    ) : (
                        <div>
                            <h1 style={{ 
                                fontSize: '28px', 
                                fontWeight: '700', 
                                background: 'linear-gradient(135deg, #700000, #a30021)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                margin: 0,
                                letterSpacing: '0.5px'
                            }}>ChatPanel</h1>
                            <p style={{
                                fontSize: '11px',
                                color: '#888',
                                margin: '4px 0 0 0',
                                textTransform: 'uppercase',
                                letterSpacing: '1px'
                            }}>Admin Dashboard</p>
                        </div>
                    )}
                </div>

                {/* Navigation Menu */}
                <nav style={{ flex: 1 }}>
                    {menuItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '14px',
                                    padding: '14px 18px',
                                    marginBottom: '6px',
                                    borderRadius: '12px',
                                    textDecoration: 'none',
                                    color: isActive ? '#ffffff' : '#a0a0a0',
                                    background: isActive 
                                        ? 'linear-gradient(135deg, #700000, #a30021)' 
                                        : 'transparent',
                                    fontWeight: isActive ? '600' : '500',
                                    fontSize: '15px',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    boxShadow: isActive ? '0 4px 12px rgba(112, 0, 0, 0.3)' : 'none'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isActive) {
                                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                        e.currentTarget.style.color = '#ffffff';
                                        e.currentTarget.style.transform = 'translateX(4px)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isActive) {
                                        e.currentTarget.style.background = 'transparent';
                                        e.currentTarget.style.color = '#a0a0a0';
                                        e.currentTarget.style.transform = 'translateX(0)';
                                    }
                                }}
                            >
                                {isActive && (
                                    <div style={{
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: '4px',
                                        background: '#ffffff',
                                        borderRadius: '0 4px 4px 0'
                                    }} />
                                )}
                                <span style={{ fontSize: '20px', flexShrink: 0 }}>{item.icon}</span>
                                <span style={{ flex: 1 }}>{item.label}</span>
                                {item.badge > 0 && (
                                    <span style={{
                                        background: '#fff',
                                        color: '#a30021',
                                        borderRadius: '10px',
                                        padding: '1px 8px',
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        minWidth: '20px',
                                        textAlign: 'center',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.2)'
                                    }}>{item.badge > 99 ? '99+' : item.badge}</span>
                                )}
                            </Link>
                        );
                    })}
                </nav>

                {/* User Profile Section */}
                <div style={{ 
                    marginTop: '20px', 
                    paddingTop: '20px', 
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                    <div style={{
                        padding: '16px',
                        marginBottom: '12px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '12px',
                            marginBottom: '8px'
                        }}>
                            <div style={{
                                width: '44px',
                                height: '44px',
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg, #700000, #a30021)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '20px',
                                color: 'white',
                                fontWeight: '700',
                                boxShadow: '0 4px 12px rgba(112, 0, 0, 0.3)'
                            }}>
                                {user?.username?.charAt(0).toUpperCase()}
                            </div>
                            <div style={{ flex: 1 }}>
                                <p style={{ 
                                    margin: 0, 
                                    fontSize: '15px', 
                                    fontWeight: '600',
                                    color: '#ffffff'
                                }}>{user?.username}</p>
                                <p style={{ 
                                    margin: 0, 
                                    fontSize: '12px', 
                                    color: '#888'
                                }}>Administrator</p>
                            </div>
                        </div>
                    </div>
                    
                    <button 
                        onClick={handleLogout}
                        style={{
                            width: '100%',
                            padding: '14px',
                            background: 'rgba(255, 255, 255, 0.05)',
                            color: '#ffffff',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #700000, #a30021)';
                            e.currentTarget.style.borderColor = 'transparent';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(112, 0, 0, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                            e.currentTarget.style.boxShadow = 'none';
                        }}
                    >
                        <span>🚪</span>
                        <span>Logout</span>
                    </button>
                </div>
            </aside>

            {/* Main Content Area - CLEAN WHITE BACKGROUND */}
            <main style={{ 
                flex: 1, 
                padding: '32px',
                overflowY: 'auto',
                position: 'relative',
                background: '#ffffff'  // Clean white background
            }}>
                <Routes>
                    <Route path="/" element={<DashboardHome />} />
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/menus" element={<MenuManagement />} />
                    <Route path="/sub-menus" element={<SubMenuManagement />} />
                    <Route path="/images" element={<ImageManagement />} />
                    <Route path="/visitors" element={<VisitorsManagement />} />
                    <Route path="/live-chat" element={<LiveChat />} />
                    <Route path="/website-scraping" element={<WebsiteScraping />} />
                    <Route path="/knowledge-base" element={<KnowledgeBaseManager />} />
                    <Route path="/acronyms" element={<AcronymsManagement />} />
                    <Route path="/widget-settings" element={<WidgetSettings />} />
                    <Route path="/settings" element={<Settings />} />
                </Routes>
            </main>
        </div>
    );
}

function DashboardHome() {
    const [stats] = useState({
        totalMenus: 5,
        widgetOpens: 16,
        menuClicks: 32,
        carouselViews: 28
    });

    return (
        <div className="fade-in-up">
            {/* Header - DARK TEXT */}
            <div style={{ marginBottom: '32px' }}>
                <h1 style={{ 
                    fontSize: '36px', 
                    fontWeight: '700', 
                    marginBottom: '8px',
                    color: '#1a1a1a',  // Dark color for white background
                    textShadow: 'none'
                }}>Dashboard</h1>
                <p style={{ 
                    fontSize: '16px', 
                    color: '#666666',  // Gray color
                    margin: 0
                }}>Welcome back! Here's what's happening today.</p>
            </div>

            {/* Stats Cards */}
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                gap: '20px',
                marginBottom: '32px'
            }}>
                <div className="stats-card">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <h3 style={{ fontSize: '14px', color: 'rgba(26,26,26,0.7)', margin: 0, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Menus</h3>
                        <span style={{ fontSize: '24px' }}>📋</span>
                    </div>
                    <p style={{ fontSize: '42px', fontWeight: '700', color: '#700000', margin: '8px 0' }}>{stats.totalMenus}</p>
                    <p style={{ fontSize: '13px', color: 'rgba(26,26,26,0.6)', margin: 0 }}>Active menu items</p>
                </div>

                <div className="stats-card">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <h3 style={{ fontSize: '14px', color: 'rgba(26,26,26,0.7)', margin: 0, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Widget Opens</h3>
                        <span style={{ fontSize: '24px' }}>🎯</span>
                    </div>
                    <p style={{ fontSize: '42px', fontWeight: '700', color: '#a30021', margin: '8px 0' }}>{stats.widgetOpens}</p>
                    <p style={{ fontSize: '13px', color: 'rgba(26,26,26,0.6)', margin: 0 }}>Total widget interactions</p>
                </div>

                <div className="stats-card">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <h3 style={{ fontSize: '14px', color: 'rgba(26,26,26,0.7)', margin: 0, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Menu Clicks</h3>
                        <span style={{ fontSize: '24px' }}>👆</span>
                    </div>
                    <p style={{ fontSize: '42px', fontWeight: '700', color: '#cc0033', margin: '8px 0' }}>{stats.menuClicks}</p>
                    <p style={{ fontSize: '13px', color: 'rgba(26,26,26,0.6)', margin: 0 }}>User engagements</p>
                </div>

                <div className="stats-card">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <h3 style={{ fontSize: '14px', color: 'rgba(26,26,26,0.7)', margin: 0, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Carousel Views</h3>
                        <span style={{ fontSize: '24px' }}>👁️</span>
                    </div>
                    <p style={{ fontSize: '42px', fontWeight: '700', color: '#700000', margin: '8px 0' }}>{stats.carouselViews}</p>
                    <p style={{ fontSize: '13px', color: 'rgba(26,26,26,0.6)', margin: 0 }}>Total carousel impressions</p>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="glass-card" style={{ marginBottom: '32px' }}>
                <h2 style={{ 
                    fontSize: '20px', 
                    fontWeight: '700', 
                    marginBottom: '20px',
                    color: '#1a1a1a'
                }}>Quick Actions</h2>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <Link to="/analytics" className="glass-button-primary" style={{
                        padding: '12px 24px',
                        background: 'linear-gradient(135deg, #700000, #a30021)',
                        color: 'white',
                        borderRadius: '12px',
                        textDecoration: 'none',
                        fontWeight: '600',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        border: 'none'
                    }}>
                        <span>📈</span>
                        <span>View Analytics</span>
                    </Link>
                    
                    <Link to="/menus" className="glass-button" style={{
                        padding: '12px 24px',
                        borderRadius: '12px',
                        textDecoration: 'none',
                        fontWeight: '600',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <span>📋</span>
                        <span>Manage Menus</span>
                    </Link>
                    
                    <Link to="/knowledge-base" className="glass-button" style={{
                        padding: '12px 24px',
                        borderRadius: '12px',
                        textDecoration: 'none',
                        fontWeight: '600',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <span>🤖</span>
                        <span>AI Knowledge Base</span>
                    </Link>
                    
                    <Link to="/widget-settings" className="glass-button" style={{
                        padding: '12px 24px',
                        borderRadius: '12px',
                        textDecoration: 'none',
                        fontWeight: '600',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <span>🎨</span>
                        <span>Widget Settings</span>
                    </Link>
                </div>
            </div>

            {/* Recent Activity */}
            <div className="glass-card">
                <h2 style={{ 
                    fontSize: '20px', 
                    fontWeight: '700', 
                    marginBottom: '20px',
                    color: '#1a1a1a'
                }}>Recent Activity</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {[
                        { action: 'New visitor registered', time: '5 minutes ago', icon: '👤', color: '#700000' },
                        { action: 'Menu item updated', time: '15 minutes ago', icon: '📝', color: '#a30021' },
                        { action: 'Widget deployed', time: '1 hour ago', icon: '🚀', color: '#cc0033' },
                        { action: 'Analytics report generated', time: '2 hours ago', icon: '📊', color: '#700000' }
                    ].map((activity, index) => (
                        <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            padding: '12px',
                            background: 'rgba(255,255,255,0.3)',
                            borderRadius: '12px',
                            border: '1px solid rgba(255,255,255,0.3)'
                        }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '12px',
                                background: `${activity.color}15`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '18px'
                            }}>
                                {activity.icon}
                            </div>
                            <div style={{ flex: 1 }}>
                                <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1a1a1a' }}>
                                    {activity.action}
                                </p>
                                <p style={{ margin: 0, fontSize: '12px', color: 'rgba(26,26,26,0.6)' }}>
                                    {activity.time}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default Dashboard;
